import { API_CONFIG } from '../api/config';
import type {
  GatewayChatRequest,
  GatewayChatResponse,
  GatewayHealthResponse,
  GatewayToolInvokeRequest,
  SSEEvent,
} from './types';

const PROXY_URL = API_CONFIG.GATEWAY.PROXY_URL;

/** Org-scoped status path per SESSION-CONTRACT (GET /api/v1/organization/:org/agent/gateway/status). */
function statusPath(organizationId?: string): string {
  if (organizationId) {
    return `/api/v1/organization/${organizationId}/agent/gateway/status`;
  }
  return `${PROXY_URL}/status`;
}

async function fetchGateway<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${PROXY_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`Gateway proxy error (${response.status}): ${errorText}`);
  }

  return response.json();
}

export const gatewayClient = {
  async chat(request: GatewayChatRequest): Promise<GatewayChatResponse> {
    return fetchGateway('/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async chatStream(
    request: GatewayChatRequest,
    callbacks: {
      onEvent: (event: SSEEvent) => void;
      onError: (error: Error) => void;
      onDone: () => void;
    },
  ): Promise<void> {
    const response = await fetch(`${PROXY_URL}/chat/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      callbacks.onError(new Error(`Gateway stream error (${response.status}): ${errorText}`));
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError(new Error('No response body'));
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              callbacks.onEvent({ event: currentEvent, data });

              if (currentEvent === 'done') {
                callbacks.onDone();
                return;
              }
              if (currentEvent === 'error') {
                callbacks.onError(new Error(data.error || 'Stream error'));
                return;
              }
            } catch {
              // Skip malformed SSE lines
            }
            currentEvent = '';
          }
        }
      }
      callbacks.onDone();
    } catch (err: any) {
      callbacks.onError(err);
    }
  },

  async checkHealth(organizationId?: string): Promise<GatewayHealthResponse> {
    try {
      const path = statusPath(organizationId);
      const response = await fetch(path, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        return { connected: false, authOk: false, error: errorText || `Status ${response.status}` };
      }
      return response.json();
    } catch {
      return { connected: false, authOk: false, error: 'Gateway unreachable' };
    }
  },

  async invokeTool(request: GatewayToolInvokeRequest): Promise<any> {
    return fetchGateway('/tools/invoke', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },
};
